import { AddressDetails, DEFAULT_ADDRESS_DETAILS, GeoCoordinates } from '../../shared/models/address.types';
import { ContactDetails, ContactPhone, DEFAULT_CONTACT_PHONE } from '../../shared/models/contact.types';

export type AgencyStatus = 'active' | 'inactive';
export type AgencyStockStatus = 'ok' | 'low' | 'out-of-stock';

export type AgencyPhone = ContactPhone;
export type AgencyContact = ContactDetails;
export type AgencyGeoCoordinates = GeoCoordinates;
export type AgencyAddress = AddressDetails;

export interface AgencyModel {
  id: string;
  name: string;
  code: string;
  city: string;
  area: string;
  address: string;
  contact: string;
  status: AgencyStatus;
  stockStatus: AgencyStockStatus;
  supervisors: number;
  agents: number;
  posCount: number;
  primaryContactName?: string;
  contactDetails: AgencyContact;
  addressDetails: AgencyAddress;
  assignedSupervisors: string[];
  assignedAgents: string[];
  assignedBtlUsers: string[];
  assignedLogisticsUsers: string[];
  assignedPos: string[];
}

export interface AgencyUpsertPayload {
  name: string;
  code: string;
  status: AgencyStatus;
  primaryContactName?: string;
  contact: AgencyContact;
  address: AgencyAddress;
  assignedSupervisors?: string[];
  assignedAgents?: string[];
  assignedBtlUsers?: string[];
  assignedLogisticsUsers?: string[];
  assignedPos?: string[];
}

export interface AgencyRecord {
  id: string;
  name: string;
  code: string;
  city: string;
  area: string;
  address: string;
  contact: string;
  status: AgencyStatus;
  stockStatus: AgencyStockStatus;
  supervisors: number;
  agents: number;
  posCount: number;
  primaryContactName?: string;
  contactDetails?: AgencyContact;
  addressDetails?: AgencyAddress;
  assignedSupervisors?: string[];
  assignedAgents?: string[];
  assignedBtlUsers?: string[];
  assignedLogisticsUsers?: string[];
  assignedPos?: string[];
}

export const DEFAULT_PHONE: AgencyPhone = DEFAULT_CONTACT_PHONE;
export const DEFAULT_ADDRESS: AgencyAddress = DEFAULT_ADDRESS_DETAILS;

export function toAgencyRecord(id: string, payload: AgencyUpsertPayload, current?: AgencyRecord): AgencyRecord {
  const flattened = flattenAgencyPayload(payload);

  return {
    id,
    stockStatus: current?.stockStatus ?? 'ok',
    supervisors: current?.supervisors ?? 0,
    agents: current?.agents ?? 0,
    posCount: current?.posCount ?? 0,
    assignedSupervisors: payload.assignedSupervisors ?? current?.assignedSupervisors ?? [],
    assignedAgents: payload.assignedAgents ?? current?.assignedAgents ?? [],
    assignedBtlUsers: payload.assignedBtlUsers ?? current?.assignedBtlUsers ?? [],
    assignedLogisticsUsers: payload.assignedLogisticsUsers ?? current?.assignedLogisticsUsers ?? [],
    assignedPos: payload.assignedPos ?? current?.assignedPos ?? [],
    ...flattened
  };
}

export function toAgencyModel(record: AgencyRecord): AgencyModel {
  return {
    ...record,
    primaryContactName: record.primaryContactName,
    contactDetails: record.contactDetails ?? parseLegacyContact(record.contact),
    addressDetails: record.addressDetails ?? parseLegacyAddress(record),
    assignedSupervisors: record.assignedSupervisors ?? [],
    assignedAgents: record.assignedAgents ?? [],
    assignedBtlUsers: record.assignedBtlUsers ?? [],
    assignedLogisticsUsers: record.assignedLogisticsUsers ?? [],
    assignedPos: record.assignedPos ?? []
  };
}

export function flattenAgencyPayload(payload: AgencyUpsertPayload): Omit<AgencyRecord, 'id' | 'stockStatus' | 'supervisors' | 'agents' | 'posCount'> {
  return {
    name: payload.name,
    code: payload.code,
    status: payload.status,
    primaryContactName: payload.primaryContactName,
    city: payload.address.locality,
    area: payload.address.area,
    address: payload.address.formattedAddress,
    contact: payload.contact.phone.number,
    contactDetails: payload.contact,
    addressDetails: payload.address
  };
}

function parseLegacyContact(contact: string): AgencyContact {
  return {
    email: '',
    phone: {
      ...DEFAULT_PHONE,
      number: contact
    }
  };
}

function parseLegacyAddress(record: Pick<AgencyRecord, 'address' | 'city' | 'area'>): AgencyAddress {
  return {
    ...DEFAULT_ADDRESS,
    formattedAddress: record.address,
    locality: record.city,
    area: record.area
  };
}
